import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as mentorApi from '../../api/mentorApi';
import { YEAR2_COUNCILS } from '../../data/year2Councils';
import {
  getRatingBandLabel,
  isRatingPresent,
  keywordSetsForRole,
  resolveYear2Criteria,
} from '../../utils/year2CriteriaUi';
import { resolveStudent } from '../../utils/studentResolution';

const AUDIT_FIELDS = [
  { key: 'opening_observations', label: 'Opening observations' },
  { key: 'council_process_notes', label: 'Council process notes' },
  { key: 'participation_quality', label: 'Participation quality' },
  { key: 'output_quality_notes', label: 'Output quality notes' },
  { key: 'recommendations', label: 'Recommendations' },
];

const ENTITY_LABELS = {
  chair: 'Chair assessment',
  deputy: 'Co-chair assessment',
  squad: 'Squad assessment',
};

// Optional note only — never gates or scores the rating. Presentation format
// (printout, video, verbal only, etc.) is the student's choice per Sangeeth's
// model; auditors rate the council presentation regardless of what, if
// anything, was produced alongside it.
const PRESENTATION_FORMAT_OPTIONS = [
  { value: '', label: 'Not noted' },
  { value: 'printout', label: 'Printout / physical handout' },
  { value: 'video', label: 'Video' },
  { value: 'research_report', label: 'Research report' },
  { value: 'verbal_only', label: 'Verbal presentation only' },
  { value: 'other', label: 'Other' },
];

function asArray(value) {
  if (Array.isArray(value)) return value;
  if (value == null) return [];
  return [value];
}

function toId(value) {
  return value == null ? '' : String(value);
}

function statusClass(status) {
  if (status === 'approved') return 'completed';
  if (status === 'rejected') return 'cancelled';
  if (status === 'submitted') return 'planned';
  return 'draft';
}

function getSquadId(squad) {
  return squad?.squad_id ?? squad?.id;
}

function getChairId(squad, role) {
  return role === 'deputy'
    ? squad?.deputy_student_id ?? squad?.deputyStudentId ?? squad?.chair_2_student_id ?? squad?.co_chair_student_id
    : squad?.chair_student_id ?? squad?.chairStudentId ?? squad?.chair_1_student_id;
}

function getAssessment(squad, role) {
  if (role === 'squad') return squad?.squad_assessment ?? squad?.squadAssessment ?? null;
  if (role === 'deputy') return squad?.deputy_assessment ?? squad?.deputyAssessment ?? null;
  return squad?.chair_assessment ?? squad?.chairAssessment ?? null;
}

function makeDraft(session, squad, role, existing = null) {
  const squadId = getSquadId(squad);
  const studentId = role === 'squad' ? null : getChairId(squad, role);
  return {
    ...(existing || {}),
    session_id: session?.id,
    cohort_id: squad?.cohort_id ?? session?.cohortId ?? null,
    council_number: existing?.council_number ?? squad?.council_number ?? session?.councilNumber ?? 1,
    squad_id: existing?.squad_id ?? squadId,
    student_id: existing?.student_id ?? studentId,
    assessment_role: existing?.assessment_role ?? (role === 'deputy' ? 'deputy' : 'chair'),
    rating: existing?.rating ?? '',
    strengths_json: asArray(existing?.strengths_json),
    coaching_json: asArray(existing?.coaching_json),
    evidence_note: existing?.evidence_note || '',
    feedback_note: existing?.feedback_note || '',
    criteria_json: existing?.criteria_json || {},
    status: existing?.status || 'draft',
  };
}

function normalizeWorkspace(payload) {
  const workspace = payload || {};
  return {
    ...workspace,
    squads: asArray(workspace.squads),
    readiness: workspace.readiness || { ready: true, editable: true, missing_chair_nominations: [] },
    sessionAudit: workspace.session_audit ?? workspace.sessionAudit ?? null,
  };
}

export default function Year2SessionView({
  session,
  user,
  evaluatingCohortId,
  onCohortChange,
  liveStudents = [],
  onBack,
  onError,
  onSuccess,
}) {
  const [workspace, setWorkspace] = useState(null);
  const [auditRecord, setAuditRecord] = useState(null);
  const [auditDraft, setAuditDraft] = useState({});
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState('');

  const councilNumber = useMemo(
    () => Number(session?.councilNumber) || Number(workspace?.session?.council_number) || 1,
    [session?.councilNumber, workspace?.session?.council_number]
  );

  const councilGuide = useMemo(
    () => YEAR2_COUNCILS.find((c) => c.number === councilNumber) || YEAR2_COUNCILS[0],
    [councilNumber]
  );

  const criteria = useMemo(
    () => resolveYear2Criteria(workspace, councilNumber),
    [workspace, councilNumber]
  );

  const groupCohortIds = useMemo(
    () => asArray(session?.groupCohortIds || workspace?.cohort_ids).map(toId).filter(Boolean),
    [session?.groupCohortIds, workspace?.cohort_ids]
  );

  const loadWorkspace = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = normalizeWorkspace(await mentorApi.getYear2EvaluationWorkspace(session.id));
      setWorkspace(loaded);
      setAuditRecord(loaded.sessionAudit);
      setAuditDraft(loaded.sessionAudit?.audit_json || {});
      const nextDrafts = {};
      loaded.squads.forEach((squad) => {
        ['chair', 'deputy', 'squad'].forEach((role) => {
          const studentId = role === 'squad' ? 'squad' : getChairId(squad, role);
          if (role !== 'squad' && !studentId) return;
          const key = `${getSquadId(squad)}:${role}:${studentId}`;
          nextDrafts[key] = makeDraft(session, squad, role, getAssessment(squad, role));
        });
      });
      setDrafts(nextDrafts);
    } catch (err) {
      onError?.(err?.message || 'Failed to load Year 2 evaluation workspace');
    } finally {
      setLoading(false);
    }
  }, [session, onError]);

  useEffect(() => {
    loadWorkspace();
  }, [loadWorkspace]);

  const editable = workspace?.readiness?.editable !== false;
  const auditLocked = !editable || ['submitted', 'approved', 'rejected'].includes(auditRecord?.status);

  const updateDraft = (key, patch) => {
    setDrafts((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), ...patch } }));
  };

  const toggleKeyword = (key, field, word) => {
    const current = drafts[key] || {};
    const list = asArray(current[field]);
    const otherField = field === 'strengths_json' ? 'coaching_json' : 'strengths_json';
    const otherList = asArray(current[otherField]);

    if (list.includes(word)) {
      updateDraft(key, { [field]: list.filter((item) => item !== word) });
      return;
    }

    updateDraft(key, {
      [field]: [...list, word],
      [otherField]: otherList.filter((item) => item !== word),
    });
  };

  const updateEntity = (key, row) => {
    setDrafts((prev) => ({ ...prev, [key]: row }));
  };

  const handleSaveAudit = async (submitAfter = false) => {
    if (!editable) return;
    setSavingKey('audit');
    try {
      const saved = await mentorApi.upsertYear2SessionAudit({
        session_id: session.id,
        audit_json: auditDraft,
        status: 'draft',
      });
      const next = submitAfter ? await mentorApi.submitYear2SessionAudit(saved.id) : saved;
      setAuditRecord(next);
      onSuccess?.(submitAfter ? 'Session audit submitted.' : 'Session audit draft saved.');
    } catch (err) {
      onError?.(err?.message || 'Failed to save session audit');
    } finally {
      setSavingKey('');
    }
  };

  const handleReopenAudit = async () => {
    if (!auditRecord?.id) return;
    setSavingKey('audit');
    try {
      const reopened = await mentorApi.reopenYear2SessionAudit(auditRecord.id);
      setAuditRecord(reopened);
      setAuditDraft(reopened?.audit_json || auditDraft);
      onSuccess?.('Session audit reopened.');
    } catch (err) {
      onError?.(err?.message || 'Failed to reopen session audit');
    } finally {
      setSavingKey('');
    }
  };

  const saveEntity = async (key, role, submitAfter = false) => {
    const draft = drafts[key];
    if (!draft || !editable) return;

    if (submitAfter && !isRatingPresent(draft.rating)) {
      onError?.('Rating (1–10) is required before submitting an assessment.');
      return;
    }

    setSavingKey(key);
    try {
      const payload = {
        ...draft,
        rating: draft.rating === '' || draft.rating == null ? null : Number(draft.rating),
        criteria_json: draft.criteria_json || {},
      };
      const saved = role === 'squad'
        ? await mentorApi.upsertYear2SquadAssessment(payload)
        : await mentorApi.upsertYear2StudentAssessment(payload);
      const next = submitAfter
        ? role === 'squad'
          ? await mentorApi.submitYear2SquadAssessment(saved.id)
          : await mentorApi.submitYear2StudentAssessment(saved.id)
        : saved;
      updateEntity(key, next);
      onSuccess?.(submitAfter ? `${ENTITY_LABELS[role]} submitted.` : `${ENTITY_LABELS[role]} draft saved.`);
    } catch (err) {
      onError?.(err?.message || `Failed to save ${ENTITY_LABELS[role].toLowerCase()}`);
    } finally {
      setSavingKey('');
    }
  };

  const reopenEntity = async (key, role) => {
    const draft = drafts[key];
    if (!draft?.id || !editable) return;
    setSavingKey(key);
    try {
      const reopened = role === 'squad'
        ? await mentorApi.reopenYear2SquadAssessment(draft.id)
        : await mentorApi.reopenYear2StudentAssessment(draft.id);
      updateEntity(key, reopened);
      onSuccess?.(`${ENTITY_LABELS[role]} reopened.`);
    } catch (err) {
      onError?.(err?.message || `Failed to reopen ${ENTITY_LABELS[role].toLowerCase()}`);
    } finally {
      setSavingKey('');
    }
  };

  const renderKeywordButtons = (key, field, words, negative = false) => {
    const values = asArray(drafts[key]?.[field]);
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {words.map((word) => (
          <button
            key={word}
            type="button"
            className={values.includes(word) ? (negative ? 'chip-selected-negative' : 'chip-selected') : 'chip-unselected'}
            disabled={!editable || ['submitted', 'approved', 'rejected'].includes(drafts[key]?.status)}
            onClick={() => toggleKeyword(key, field, word)}
          >
            {word}
          </button>
        ))}
      </div>
    );
  };

  const renderAssessmentForm = (squad, role, studentId = null) => {
    const key = `${getSquadId(squad)}:${role}:${role === 'squad' ? 'squad' : studentId}`;
    const draft = drafts[key] || makeDraft(session, squad, role);
    const isLocked = !editable || ['submitted', 'approved', 'rejected'].includes(draft.status);
    const isSaving = savingKey === key;
    const student = studentId ? resolveStudent(studentId, liveStudents) : null;
    const keywordSets = keywordSetsForRole(criteria, role);
    const ratingBandLabel = getRatingBandLabel(draft.rating, criteria.rating_scale);
    const canSubmit = isRatingPresent(draft.rating);
    // Council 4 ("The Proposal") is presented by two co-equal chairs — first
    // chair for the Research/Social Startup Report, second for the Policy
    // Brief — rather than a chair + deputy pairing. Same data shape
    // (chair_student_id / deputy_student_id), label-only distinction.
    const isSecondChairCouncil = role === 'deputy' && councilNumber === 4;
    const entityLabel = isSecondChairCouncil ? 'Second Chair assessment (Policy Brief)' : ENTITY_LABELS[role];

    return (
      <div className="wizard-card" style={{ padding: '18px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', alignItems: 'flex-start', marginBottom: '14px' }}>
          <div>
            <h4 style={{ margin: 0 }}>{entityLabel}</h4>
            <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
              {student ? student.name : squad?.name || squad?.squad_name || 'Whole squad'}
            </p>
            {isSecondChairCouncil && (
              <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '12px' }}>
                Presents the Policy Brief, separate from the first Chair's Report.
              </p>
            )}
            <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: '12px', fontStyle: 'italic' }}>
              Rate based on the council presentation. Written or LMS output is optional and does not affect this rating.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            {draft.is_revision && <span className="role-badge revised">Revised v{draft.revision_number || 1}</span>}
            <span className={`status-badge status-${statusClass(draft.status)}`}>{draft.status || 'draft'}</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr', gap: '14px', alignItems: 'start' }}>
          <div className="form-field-group">
            <label>Rating (1-10)</label>
            <input
              type="number"
              min={1}
              max={10}
              className="form-control-input"
              disabled={isLocked}
              value={draft.rating || ''}
              onChange={(e) => updateDraft(key, { rating: e.target.value })}
            />
            {ratingBandLabel && (
              <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                {ratingBandLabel}
              </p>
            )}
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 700, marginBottom: '6px' }}>Strengths</label>
            {renderKeywordButtons(key, 'strengths_json', keywordSets.strengths)}
          </div>
        </div>

        <div style={{ marginTop: '14px' }}>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: '6px' }}>Coaching areas</label>
          {renderKeywordButtons(key, 'coaching_json', keywordSets.coaching, true)}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px', marginTop: '14px' }}>
          <div className="form-field-group">
            <label>Evidence note</label>
            <textarea
              className="form-control-textarea"
              rows={3}
              disabled={isLocked}
              value={draft.evidence_note || ''}
              onChange={(e) => updateDraft(key, { evidence_note: e.target.value })}
            />
          </div>
          <div className="form-field-group">
            <label>Feedback note</label>
            <textarea
              className="form-control-textarea"
              rows={3}
              disabled={isLocked}
              value={draft.feedback_note || ''}
              onChange={(e) => updateDraft(key, { feedback_note: e.target.value })}
            />
          </div>
        </div>

        <div className="form-field-group" style={{ marginTop: '14px', maxWidth: '320px' }}>
          <label>Presentation format (optional)</label>
          <select
            className="form-control-select"
            disabled={isLocked}
            value={draft.criteria_json?.presentation_format || ''}
            onChange={(e) => updateDraft(key, {
              criteria_json: { ...(draft.criteria_json || {}), presentation_format: e.target.value },
            })}
          >
            {PRESENTATION_FORMAT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
            For your own reference only — does not affect the rating.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end', marginTop: '14px' }}>
          {['submitted', 'approved', 'rejected'].includes(draft.status) && (
            <button
              type="button"
              className="btn-secondary-action"
              disabled={!editable || isSaving || !draft.id}
              onClick={() => reopenEntity(key, role)}
            >
              Re-evaluate
            </button>
          )}
          <button
            type="button"
            className="btn-form-toggle-outlined"
            disabled={isLocked || isSaving}
            onClick={() => saveEntity(key, role, false)}
          >
            {isSaving ? 'Saving...' : 'Save draft'}
          </button>
          <button
            type="button"
            className="btn-primary-action"
            disabled={isLocked || isSaving || !canSubmit}
            onClick={() => saveEntity(key, role, true)}
          >
            Submit
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="evaluation-workspace animate-fade-in" style={{ paddingBottom: '120px', paddingTop: '24px' }}>
      <button type="button" className="btn-back-link" onClick={onBack}>
        ← Back to My Sessions
      </button>

      <div className="evaluation-sticky-header">
        <div className="sticky-header-container">
          <div className="sticky-header-meta">
            <h2 className="sticky-title">Year 2 Evaluation — {session?.sessionType || 'Session'}</h2>
            <p className="sticky-subtitle">
              Council {criteria.council_number}: {criteria.squad.title} • {criteria.squad.tagline} • {session?.timeSlot || 'Time slot pending'} • {session?.schoolName || 'School'}
            </p>
          </div>
          {session?.meetingLink && (
            <a href={session.meetingLink} target="_blank" rel="noopener noreferrer" className="btn-sticky-join">
              Join link
            </a>
          )}
        </div>
      </div>

      {groupCohortIds.length > 1 && (
        <div className="wizard-card" style={{ padding: '14px', marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: 700, marginBottom: '6px' }}>Group-session cohort</label>
          <select
            className="form-control-select"
            value={evaluatingCohortId || session?.cohortId || groupCohortIds[0]}
            onChange={(e) => onCohortChange?.(e.target.value)}
          >
            {groupCohortIds.map((id) => (
              <option key={id} value={id}>Cohort {id}</option>
            ))}
          </select>
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading Year 2 workspace...</p>
      ) : !workspace ? (
        <div className="empty-state-card"><p className="empty-state-description">Year 2 workspace could not be loaded.</p></div>
      ) : (
        <>
          {workspace.readiness?.ready === false && (
            <div className="rejection-banner" style={{ marginBottom: '16px' }}>
              <strong>Readiness blocked.</strong>{' '}
              {asArray(workspace.readiness?.missing_chair_nominations).length > 0
                ? `Missing chair nominations: ${asArray(workspace.readiness.missing_chair_nominations).join(', ')}.`
                : 'Chair nominations are incomplete.'}
              {workspace.readiness?.editable === false && <span> This workspace is read-only until setup is complete.</span>}
            </div>
          )}

          <div className="wizard-card" style={{ marginBottom: '20px', padding: '18px' }}>
            <h3 style={{ marginTop: 0 }}>Council {criteria.council_number} — {criteria.squad.title}</h3>
            <p style={{ margin: '6px 0 12px', color: 'var(--text-muted)' }}>{criteria.squad.tagline}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {asArray(councilGuide.outputs).map((output) => (
                <span key={output} className="status-badge status-planned" style={{ textTransform: 'none' }}>
                  {String(output).replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          </div>

          <div className="wizard-card" style={{ padding: '18px', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h3 style={{ margin: 0 }}>Session observation audit</h3>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {auditRecord?.is_revision && <span className="role-badge revised">Revised v{auditRecord.revision_number || 1}</span>}
                <span className={`status-badge status-${statusClass(auditRecord?.status || 'draft')}`}>{auditRecord?.status || 'draft'}</span>
              </div>
            </div>
            {AUDIT_FIELDS.map((field) => (
              <div key={field.key} className="form-field-group" style={{ marginBottom: '14px' }}>
                <label htmlFor={`audit-${field.key}`}>{field.label}</label>
                <textarea
                  id={`audit-${field.key}`}
                  className="form-control-textarea"
                  rows={3}
                  disabled={auditLocked}
                  value={auditDraft[field.key] || ''}
                  onChange={(e) => setAuditDraft((prev) => ({ ...prev, [field.key]: e.target.value }))}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {['submitted', 'approved', 'rejected'].includes(auditRecord?.status) && (
                <button type="button" className="btn-secondary-action" disabled={!editable || savingKey === 'audit' || !auditRecord?.id} onClick={handleReopenAudit}>
                  Reopen audit
                </button>
              )}
              <button type="button" className="btn-form-toggle-outlined" disabled={auditLocked || savingKey === 'audit'} onClick={() => handleSaveAudit(false)}>
                {savingKey === 'audit' ? 'Saving...' : 'Save draft'}
              </button>
              <button type="button" className="btn-primary-action" disabled={auditLocked || savingKey === 'audit'} onClick={() => handleSaveAudit(true)}>
                Submit audit
              </button>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
            {workspace.squads.length === 0 ? (
              <div className="empty-state-card"><p className="empty-state-description">No Year 2 squads are configured for this session.</p></div>
            ) : workspace.squads.map((squad, index) => {
              const chairId = getChairId(squad, 'chair');
              const deputyId = getChairId(squad, 'deputy');
              const chair = chairId ? resolveStudent(chairId, liveStudents) : null;
              const deputy = deputyId ? resolveStudent(deputyId, liveStudents) : null;

              // Council 5 ("The Crucible") is an inter-cohort live defence —
              // squads present as a team to a panel of student committees,
              // there is no individual Chair/Deputy role for this council.
              // Rate the squad's defence as a whole; suppress the per-chair
              // forms rather than showing a misleading "missing" state.
              const isCrucibleCouncil = councilNumber === 5;

              return (
                <section key={getSquadId(squad) || index} className="wizard-card" style={{ padding: '18px' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <h3 style={{ margin: 0 }}>{squad.name || squad.squad_name || `Squad ${index + 1}`}</h3>
                    <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: '13px' }}>
                      Topic: {squad.topic || squad.policy_topic || 'Not set'}
                      {!isCrucibleCouncil && ` • Chair: ${chair?.name || 'Missing'} • Co-chair: ${deputy?.name || 'Not nominated'}`}
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {isCrucibleCouncil ? (
                      <div className="empty-state-card" style={{ padding: '18px' }}>
                        Council 5 is a live, inter-cohort defence — there is no Chair or Co-chair role for this council. Rate the squad's defence as a whole below.
                      </div>
                    ) : (
                      <>
                        {chairId ? renderAssessmentForm(squad, 'chair', chairId) : (
                          <div className="empty-state-card" style={{ padding: '18px' }}>Chair nomination missing for this squad.</div>
                        )}
                        {deputyId ? renderAssessmentForm(squad, 'deputy', deputyId) : (
                          <div className="empty-state-card" style={{ padding: '18px' }}>Co-chair nomination is optional and not set for this squad.</div>
                        )}
                      </>
                    )}
                    {renderAssessmentForm(squad, 'squad')}
                  </div>
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
